/**
 * HeritageHome — the unified landing page for Family Tree AND
 * Celebrate a Life, styled after IndianFamilyTreeHome.
 *
 * Goal: rescue users from "the socialpulse maze" — give them one
 * warm, editorial entry point into everything heritage-related:
 * ancestral trees, memorials, today's anniversaries, cultural
 * heritage articles, the family book flipbook, and community
 * participation prompts.
 *
 * Route: /heritage  (also aliased at /family-and-memorials)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http, resolveImage } from "@/lib/api";
import FeaturedTestTrees from "@/components/FeaturedTestTrees";
import {
  Loader2, TreePine, Flower2, Sparkles, ArrowRight, BookOpen,
  Users, Heart, Compass, MapPin, Camera, ScrollText, Home,
  Feather, Church, Landmark, Upload, Globe2, Crown, HardHat,
  Stethoscope, Palette, Shield,
} from "lucide-react";

const INDIA_URL = "https://legacy-archive-13.emergent.host";

// Heritage palette — warm parchment, deep sepia, muted saffron.
const P = {
  bg:        "#FBF5EA",
  ink:       "#2A1D10",
  inkSoft:   "#5C4A32",
  parchment: "#F0E4CE",
  saffron:   "#B25D38",
  saffronSoft:"#F6DCC5",
  green:     "#0F6B45",
  greenSoft: "#CFEBD9",
  rose:      "#8B3A3A",
  gold:      "#B98A2A",
};

export default function HeritageHome() {
  const [params] = useSearchParams();
  const region = (params.get("region") || "").toLowerCase();
  const country = (params.get("country") || "").toLowerCase();
  const scope = region || country || null;

  const [data, setData] = useState({
    trees: null, memorials: null, anniversaries: null,
    notable: null, historic: null, historicalFigures: null,
  });

  useEffect(() => {
    let alive = true;
    const treeParams = { limit: 8, visibility: "public" };
    const memParams  = { limit: 6, status: "published" };
    if (region)  { treeParams.region  = region;  memParams.region  = region;  }
    if (country) { treeParams.country = country; memParams.country = country; }
    Promise.allSettled([
      http.get("/family-trees", { params: treeParams }),
      http.get("/memorials", { params: memParams }),
      http.get("/memorials/today-anniversaries", { params: { limit: 8 } }).catch(() => null),
      http.get("/family-trees", { params: { ...treeParams, sort: "-member_count", limit: 4 } }).catch(() => null),
      http.get("/family-trees", { params: { ...treeParams, sort: "created_at", limit: 4 } }).catch(() => null),
      http.get("/memorials", { params: { ...memParams, tag: "historical", limit: 4 } }).catch(() => null),
    ]).then(([t, m, a, notable, historic, historicalFigures]) => {
      if (!alive) return;
      const pick = (r) => r?.status === "fulfilled" ? (r.value?.data?.items || r.value?.data || []) : [];
      setData({
        trees:             pick(t),
        memorials:         pick(m),
        anniversaries:     a?.status === "fulfilled" ? (a.value?.data?.items || a.value?.data || []) : [],
        notable:           pick(notable),
        historic:          pick(historic),
        historicalFigures: pick(historicalFigures),
      });
    });
    return () => { alive = false; };
  }, [region, country]);

  const scopeLabel = useMemo(() => {
    if (region === "kerala") return "Kerala";
    if (region) return region.charAt(0).toUpperCase() + region.slice(1);
    if (country) return country.toUpperCase();
    return null;
  }, [region, country]);

  const pageTitle = `${scopeLabel ? `${scopeLabel} Heritage` : "Heritage"} — Family Trees, Memorials & Living History on Social Pulse`;

  return (
    <div style={{ background: P.bg, color: P.ink, minHeight: "100vh" }} data-testid="heritage-home">
      <Helmet>
        <title>{pageTitle} — Every Family Has a Story Worth Preserving | SocialPulse</title>
        <meta name="description"
              content="Build your family tree, honour loved ones, and preserve the stories of your village, culture, and traditions on SocialPulse — the global AI community and family discovery platform. Every family has a story worth preserving." />
        <meta name="tagline" content="Every Family Has a Story Worth Preserving." />
        <meta property="og:title" content="Family Tree | SocialPulse — The Global AI Community & Family Discovery Platform" />
        <meta property="og:description" content="Every family has a story worth preserving. Build your tree, honour ancestors, and connect generations across the world." />
      </Helmet>

      <ThinNav />
      <Hero scopeLabel={scopeLabel} />
      <QuickActions region={region} />
      <TodaysMemories items={data.anniversaries} />
      <FamilyTreeHub trees={data.trees} notable={data.notable} historic={data.historic} scopeLabel={scopeLabel} country={country} />
      <CelebrateHub items={data.memorials} historicalFigures={data.historicalFigures} scopeLabel={scopeLabel} />
      <HeritageSections />
      <MemoryWall />
      <ResearchCorner />
      <FeaturedTestTrees
        palette={P}
        title="Featured family trees"
        subtitle="Two documented family archives you can browse now — used to test the platform end-to-end."
      />
      <BottomCTA />
    </div>
  );
}


/* ---------------------------------------------------------------- */
/*                     THIN SIBLING-STYLE NAV                       */
/* ---------------------------------------------------------------- */
function ThinNav() {
  return (
    <header
      className="sticky top-0 z-30 border-b backdrop-blur"
      style={{
        background: `${P.bg}EE`,
        borderColor: P.saffronSoft,
      }}
      data-testid="heritage-thin-nav"
    >
      <div className="max-w-6xl mx-auto px-4 h-11 flex items-center justify-between gap-4 text-sm">
        <Link to="/heritage" className="flex items-center gap-1.5 font-display font-bold"
              style={{ color: P.ink }} data-testid="heritage-nav-brand">
          <Feather className="w-4 h-4" style={{ color: P.saffron }} />
          <span>Heritage</span>
          <span className="hidden sm:inline text-[11px] font-normal opacity-60 ml-1">
            · Family Trees & Celebrate a Life
          </span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
          <Link to="/family-trees" className="opacity-80 hover:opacity-100 whitespace-nowrap"
                data-testid="heritage-nav-trees">Family trees</Link>
          <Link to="/obituaries" className="opacity-80 hover:opacity-100 whitespace-nowrap"
                data-testid="heritage-nav-obituaries">Obituaries</Link>
          <Link to="/celebrate/browse" className="opacity-80 hover:opacity-100 whitespace-nowrap"
                data-testid="heritage-nav-memorials">Memorials</Link>
          <span className="hidden md:inline opacity-30">·</span>
          <a href={INDIA_URL} target="_blank" rel="noreferrer"
             className="flex items-center gap-1 opacity-90 hover:opacity-100 whitespace-nowrap font-semibold"
             style={{ color: P.saffron }}
             data-testid="heritage-nav-india">
            <Globe2 className="w-3.5 h-3.5" /> India
          </a>
          <Link to="/global-family-trees"
                className="flex items-center gap-1 opacity-90 hover:opacity-100 whitespace-nowrap font-semibold"
                style={{ color: P.green }}
                data-testid="heritage-nav-global">
            <Globe2 className="w-3.5 h-3.5" /> Global
          </Link>
          <span className="hidden md:inline opacity-30">·</span>
          <Link to="/" className="opacity-70 hover:opacity-100 whitespace-nowrap text-xs"
                data-testid="heritage-nav-socialpulse">
            <span className="opacity-60">↗</span> Social Pulse
          </Link>
          <Link to="/food-pulse" className="opacity-70 hover:opacity-100 whitespace-nowrap text-xs"
                data-testid="heritage-nav-foodpulse">
            <span className="opacity-60">↗</span> Food Pulse
          </Link>
        </nav>
      </div>
    </header>
  );
}


/* ---------------------------------------------------------------- */
/*                             HERO                                 */
/* ---------------------------------------------------------------- */
function Hero({ scopeLabel }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${P.parchment} 0%, ${P.bg} 100%)`,
        borderBottom: `1px solid ${P.saffronSoft}`,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20 text-center">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5"
             style={{ background: "#FFF3E0", color: P.saffron }}>
          <Sparkles className="w-3.5 h-3.5" />
          <span className="text-[11px] font-mono uppercase tracking-wider">
            {scopeLabel ? `${scopeLabel} heritage archive` : "Living heritage archive"}
          </span>
        </div>
        <h1 className="font-display font-bold leading-[1.05] mx-auto max-w-3xl text-4xl sm:text-5xl lg:text-6xl">
          {scopeLabel
            ? <>Roots and remembrance from <span style={{ color: P.saffron }}>{scopeLabel}</span>.</>
            : <>Roots, remembrance, and <span style={{ color: P.saffron }}>the stories that made us</span>.</>}
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg"
           style={{ color: P.inkSoft }}>
          Build your family tree. Honour a life. Preserve your village's
          traditions, recipes, and photographs. Social Pulse Heritage is
          the archive your grandchildren will thank you for.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/family-trees/new"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold shadow-sm hover:shadow-md transition text-white"
                style={{ background: P.saffron }}
                data-testid="heritage-cta-tree">
            <TreePine className="w-4 h-4" /> Start a family tree
          </Link>
          <Link to="/obituaries/submit"
                className="inline-flex flex-col items-center rounded-full px-6 py-2.5 font-semibold shadow-sm hover:shadow-md transition text-white"
                style={{ background: "#8B5CF6" }}
                data-testid="heritage-cta-obituary">
            <span className="inline-flex items-center gap-2 leading-none">
              <Feather className="w-4 h-4" /> Add an Obituary
            </span>
            <span className="text-[11px] opacity-90 mt-0.5 font-normal">(Death Announcement)</span>
          </Link>
          <Link to="/celebrate/submit"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold shadow-sm hover:shadow-md transition text-white"
                style={{ background: P.rose }}
                data-testid="heritage-cta-memorial">
            <Flower2 className="w-4 h-4" /> Create a memorial
          </Link>
          <Link to="/family-trees/new?import=gedcom"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border hover:bg-white transition"
                style={{ borderColor: P.green, color: P.green }}
                data-testid="heritage-cta-gedcom">
            <Upload className="w-4 h-4" /> Upload GEDCOM
          </Link>
        </div>
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                       FAMILY TREE HUB                            */
/* ---------------------------------------------------------------- */
const TREE_REGIONS_IN = [
  { slug: "kerala",       label: "Kerala" },
  { slug: "tamil-nadu",   label: "Tamil Nadu" },
  { slug: "karnataka",    label: "Karnataka" },
  { slug: "punjab",       label: "Punjab" },
  { slug: "maharashtra",  label: "Maharashtra" },
  { slug: "west-bengal",  label: "West Bengal" },
  { slug: "gujarat",      label: "Gujarat" },
  { slug: "uttar-pradesh",label: "Uttar Pradesh" },
];

const TREE_REGIONS_US = [
  { slug: "california",   label: "California" },
  { slug: "new-york",     label: "New York" },
  { slug: "texas",        label: "Texas" },
  { slug: "florida",      label: "Florida" },
  { slug: "illinois",     label: "Illinois" },
  { slug: "massachusetts",label: "Massachusetts" },
  { slug: "washington",   label: "Washington" },
  { slug: "new-jersey",   label: "New Jersey" },
];

const TREE_REGIONS_UK = [
  { slug: "england",   label: "England" },
  { slug: "scotland",  label: "Scotland" },
  { slug: "wales",     label: "Wales" },
  { slug: "northern-ireland", label: "Northern Ireland" },
  { slug: "london",    label: "London" },
];

const IMMIGRATION_ERAS_US = [
  { slug: "colonial",  label: "Colonial era" },
  { slug: "ellis-island", label: "Ellis Island (1892–1954)" },
  { slug: "post-1965", label: "Post-1965 wave" },
  { slug: "h1b",       label: "H-1B / modern professional" },
];

const COMMUNITIES = [
  { key: "farmers",         label: "Farming families",   Icon: HardHat },
  { key: "teachers",        label: "Teachers & scholars",Icon: BookOpen },
  { key: "doctors",         label: "Healers & doctors",  Icon: Stethoscope },
  { key: "artists",         label: "Artists & musicians",Icon: Palette },
  { key: "military",        label: "Military families",  Icon: Shield },
  { key: "freedom-fighters",label: "Freedom fighters",   Icon: Crown },
  { key: "clergy",          label: "Religious leaders",  Icon: Church },
  { key: "diaspora",        label: "Diaspora families",  Icon: Globe2 },
];

function _regionsFor(country) {
  const c = (country || "").toLowerCase();
  if (c === "us") return TREE_REGIONS_US;
  if (c === "gb" || c === "uk") return TREE_REGIONS_UK;
  return TREE_REGIONS_IN;
}

function FamilyTreeHub({ trees, notable, historic, scopeLabel, country }) {
  if (trees === null) return <SkeletonSection />;
  const rows = (trees || []).slice(0, 8);
  const regionChips = _regionsFor(country);
  const showEras = country === "us";
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="heritage-family-trees">
      <SectionHeader
        Icon={TreePine}
        color={P.saffron}
        title={scopeLabel ? `${scopeLabel} family trees` : "Family trees to explore"}
        subtitle="Public family trees you can view, contribute to, or use as inspiration for your own."
        seeMore={{ to: "/family-trees", label: "All trees" }}
      />
      {rows.length === 0
        ? <EmptyBlock icon={TreePine} label="No public trees yet — be the first to plant one." cta={{to:"/family-trees/new", label:"Start a family tree"}} />
        : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {rows.map((t) => (
              <Link key={t.id || t.slug}
                    to={`/family/${t.slug || t.id}`}
                    className="rounded-xl border bg-white p-4 flex flex-col gap-2 hover:shadow-md transition"
                    style={{ borderColor: P.saffronSoft }}
                    data-testid={`heritage-tree-${t.slug || t.id}`}>
                <div className="flex items-center gap-2">
                  <TreePine className="w-4 h-4 shrink-0" style={{ color: P.saffron }} />
                  <div className="font-semibold truncate">{t.name}</div>
                </div>
                <div className="text-[11px] font-mono opacity-70 truncate">
                  {t.origin_state ? `${t.origin_state} · ` : ""}
                  {t.member_count ? `${t.member_count} members` : "new tree"}
                </div>
                {t.summary && <div className="text-xs opacity-80 line-clamp-2">{t.summary}</div>}
              </Link>
            ))}
          </div>
        )}

      {/* Notable Families (largest trees by member count) */}
      {(notable || []).length > 0 && (
        <div className="mt-8" data-testid="heritage-notable-families">
          <SubHeader color={P.rose} title="Notable families"
                     subtitle="Trees with the deepest documented lineages." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
            {(notable || []).slice(0, 4).map((t) => (
              <Link key={t.id || t.slug} to={`/family/${t.slug || t.id}`}
                    className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2 hover:shadow-md"
                    style={{ borderColor: P.saffronSoft }}>
                <Crown className="w-4 h-4" style={{ color: P.gold }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[10px] opacity-60">{t.member_count || 0} members</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Historic Families (recent-add stubs today; behaves like a
          "join the archive" invitation when API returns nothing) */}
      {(historic || []).length > 0 && (
        <div className="mt-6" data-testid="heritage-historic-families">
          <SubHeader color={P.green} title="Historic families"
                     subtitle="Trees whose origins predate living memory." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
            {(historic || []).slice(0, 4).map((t) => (
              <Link key={t.id || t.slug} to={`/family/${t.slug || t.id}`}
                    className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2 hover:shadow-md"
                    style={{ borderColor: P.saffronSoft }}>
                <ScrollText className="w-4 h-4" style={{ color: P.green }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[10px] opacity-60">
                    {t.origin_state || t.earliest_year || "Historic archive"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* By Region — chip strip (country-aware) */}
      <div className="mt-8" data-testid="heritage-by-region">
        <SubHeader color={P.saffron}
                   title={country === "us" ? "By US state" : country === "gb" ? "By UK region" : "By region"}
                   subtitle="Filter trees rooted in a specific area of the country." />
        <div className="flex flex-wrap gap-2">
          {regionChips.map((r) => (
            <Link key={r.slug}
                  to={`/heritage?region=${r.slug}${country ? `&country=${country}` : ""}`}
                  className="rounded-full border bg-white px-3 py-1 text-xs hover:bg-[#FFF3E0]"
                  style={{ borderColor: P.saffronSoft, color: P.ink }}
                  data-testid={`heritage-region-${r.slug}`}>
              <MapPin className="w-3 h-3 inline mr-1" style={{ color: P.saffron }} />
              {r.label}
            </Link>
          ))}
        </div>
      </div>

      {/* US-only: Immigration eras */}
      {showEras && (
        <div className="mt-6" data-testid="heritage-by-era">
          <SubHeader color={P.rose} title="By immigration era"
                     subtitle="Every American family arrived in a moment — find yours." />
          <div className="flex flex-wrap gap-2">
            {IMMIGRATION_ERAS_US.map((e) => (
              <Link key={e.slug}
                    to={`/family-trees?era=${e.slug}&country=us`}
                    className="rounded-full border bg-white px-3 py-1 text-xs hover:bg-[#FFF3E0]"
                    style={{ borderColor: P.saffronSoft, color: P.ink }}
                    data-testid={`heritage-era-${e.slug}`}>
                <ScrollText className="w-3 h-3 inline mr-1" style={{ color: P.rose }} />
                {e.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* By Community — icon chip strip */}
      <div className="mt-6" data-testid="heritage-by-community">
        <SubHeader color={P.green} title="By community"
                   subtitle="Trees grouped by the traditions and occupations that shaped them." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {COMMUNITIES.map((c) => {
            const Icon = c.Icon;
            return (
              <Link key={c.key}
                    to={`/family-trees?community=${c.key}`}
                    className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2 hover:shadow-md"
                    style={{ borderColor: P.saffronSoft }}
                    data-testid={`heritage-community-${c.key}`}>
                <Icon className="w-4 h-4 shrink-0" style={{ color: P.green }} />
                <span className="text-sm truncate">{c.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                       CELEBRATE HUB                              */
/* ---------------------------------------------------------------- */
const MEMORIAL_TAGS = [
  { key: "recent",        label: "Recent tributes",       Icon: Flower2, to: "/celebrate/browse?sort=recent" },
  { key: "historical",    label: "Historical figures",    Icon: Landmark, to: "/celebrate/browse?tag=historical" },
  { key: "military",      label: "Military memorials",    Icon: Shield,   to: "/celebrate/browse?tag=military" },
  { key: "healthcare",    label: "Healthcare heroes",     Icon: Stethoscope, to: "/celebrate/browse?tag=healthcare" },
  { key: "community",     label: "Community memorials",   Icon: Users,    to: "/celebrate/browse?tag=community" },
  { key: "teachers",      label: "Teachers & mentors",    Icon: BookOpen, to: "/celebrate/browse?tag=teachers" },
  { key: "public-servants",label:"Public servants",       Icon: Landmark, to: "/celebrate/browse?tag=public-servants" },
  { key: "parents",       label: "Parents & grandparents",Icon: Heart,    to: "/celebrate/browse?tag=parents" },
];

function CelebrateHub({ items, historicalFigures, scopeLabel }) {
  if (items === null) return <SkeletonSection />;
  const rows = (items || []).slice(0, 6);
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="heritage-celebrate">
      <SectionHeader
        Icon={Flower2}
        color={P.rose}
        title={scopeLabel ? `${scopeLabel} memorials` : "Celebrate a life"}
        subtitle="Memorials, obituaries, and tribute pages honouring those who shaped their families and communities."
        seeMore={{ to: "/celebrate/browse", label: "All memorials" }}
      />
      {rows.length === 0
        ? <EmptyBlock icon={Flower2} label="No memorials yet — help start the archive." cta={{to:"/celebrate/submit", label:"Create a memorial"}} />
        : (
          <div className="grid md:grid-cols-3 gap-3">
            {rows.map((m) => (
              <Link key={m.id || m.slug}
                    to={`/celebrate/${m.slug || m.id}`}
                    className="rounded-xl border bg-white overflow-hidden hover:shadow-md transition flex flex-col"
                    style={{ borderColor: P.saffronSoft }}
                    data-testid={`heritage-memorial-${m.slug || m.id}`}>
                {m.photo || m.hero_image
                  ? <img src={resolveImage(m.photo || m.hero_image)} alt="" className="w-full h-32 object-cover" />
                  : <div className="w-full h-32 flex items-center justify-center"
                         style={{ background: P.parchment }}>
                      <Flower2 className="w-8 h-8" style={{ color: P.rose }} />
                    </div>}
                <div className="p-3 flex flex-col gap-1">
                  <div className="font-semibold truncate">{m.name || m.title}</div>
                  <div className="text-[11px] font-mono opacity-70 truncate">
                    {m.date_of_birth ? `b. ${(m.date_of_birth || "").slice(0,4)}` : ""}
                    {m.date_of_death ? ` · d. ${(m.date_of_death || "").slice(0,4)}` : ""}
                    {m.location ? ` · ${m.location}` : ""}
                  </div>
                  {m.summary && <div className="text-xs opacity-80 line-clamp-2">{m.summary}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}

      {/* Historical Figures (curated tag) */}
      {(historicalFigures || []).length > 0 && (
        <div className="mt-8" data-testid="heritage-historical-figures">
          <SubHeader color={P.gold} title="Historical figures"
                     subtitle="Memorials for the ancestors whose lives shaped the record." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
            {(historicalFigures || []).slice(0, 4).map((m) => (
              <Link key={m.id || m.slug} to={`/celebrate/${m.slug || m.id}`}
                    className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2 hover:shadow-md"
                    style={{ borderColor: P.saffronSoft }}>
                <Landmark className="w-4 h-4" style={{ color: P.gold }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{m.name || m.title}</div>
                  <div className="text-[10px] opacity-60 truncate">
                    {m.date_of_birth ? `${(m.date_of_birth || "").slice(0,4)}` : ""}
                    {m.date_of_death ? `–${(m.date_of_death || "").slice(0,4)}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Browse memorials by category */}
      <div className="mt-8" data-testid="heritage-memorial-tags">
        <SubHeader color={P.rose} title="Browse memorials by category"
                   subtitle="Every life is worth remembering. Find the ones that resonate with you." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {MEMORIAL_TAGS.map((t) => {
            const Icon = t.Icon;
            return (
              <Link key={t.key} to={t.to}
                    className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2 hover:shadow-md"
                    style={{ borderColor: P.saffronSoft }}
                    data-testid={`heritage-memorial-tag-${t.key}`}>
                <Icon className="w-4 h-4 shrink-0" style={{ color: P.rose }} />
                <span className="text-sm truncate">{t.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                    QUICK ACTIONS STRIP                           */
/* ---------------------------------------------------------------- */
function QuickActions({ region }) {
  const ITEMS = [
    { to: "/family-trees",     Icon: TreePine, label: "Browse family trees",     color: P.saffron },
    { to: "/obituaries",       Icon: Feather,  label: "Browse Obituaries",       color: "#8B5CF6" },
    { to: "/celebrate/browse", Icon: Flower2,  label: "Browse memorials",        color: P.rose    },
    { to: region === "kerala" ? "/kin-network" : "/kin-network",
      Icon: Users, label: region === "kerala" ? "Kerala Kin Network" : "Kin Network word-cloud", color: P.green   },
    { to: "/weather",          Icon: Compass,  label: "Weather Almanac",         color: P.gold    },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ITEMS.map(({ to, Icon, label, color }) => (
          <Link key={label} to={to}
                className="rounded-xl border bg-white p-4 flex items-center gap-3 hover:shadow-md transition"
                style={{ borderColor: P.saffronSoft }}
                data-testid={`heritage-quick-${label.split(" ").join("-").toLowerCase()}`}>
            <Icon className="w-5 h-5 shrink-0" style={{ color }} />
            <span className="text-sm font-semibold" style={{ color: P.ink }}>{label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*               TODAY'S MEMORIES (anniversary strip)               */
/* ---------------------------------------------------------------- */
function TodaysMemories({ items }) {
  if (items === null) return <SkeletonSection />;
  const rows = (items || []).slice(0, 8);
  if (rows.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="heritage-todays-memories">
      <SectionHeader
        Icon={Heart}
        color={P.rose}
        title="Today's memories"
        subtitle="Birthdays, memorial anniversaries, and milestones observed today across the Social Pulse community."
      />
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((it, i) => (
          <Link key={i}
                to={it.memorial_slug ? `/celebrate/${it.memorial_slug}`
                                     : it.tree_slug ? `/family/${it.tree_slug}` : "#"}
                className="rounded-xl border bg-white p-4 flex items-center gap-3 hover:shadow-md transition"
                style={{ borderColor: P.saffronSoft }}
                data-testid={`heritage-memory-${i}`}>
            {it.photo
              ? <img src={resolveImage(it.photo)} alt="" className="w-12 h-12 rounded-full object-cover" />
              : <div className="w-12 h-12 rounded-full flex items-center justify-center"
                     style={{ background: P.parchment }}>
                  <Flower2 className="w-5 h-5" style={{ color: P.rose }} />
                </div>}
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{it.name || it.title || "In memoriam"}</div>
              <div className="text-[11px] font-mono opacity-70 truncate">
                {(it.kind || it.event_type || "anniversary").replace(/_/g, " ")}
                {it.year ? ` · ${it.year}` : ""}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 opacity-40" />
          </Link>
        ))}
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                       HERITAGE EDITORIAL BLOCKS                  */
/* ---------------------------------------------------------------- */
function HeritageSections() {
  const BLOCKS = [
    {
      Icon: MapPin,
      title: "Discover your heritage",
      body:  "Explore your ancestral roots through interactive maps, migration timelines, and stories about the villages, temples, and homes that shaped your family.",
      to:    "/kin-network",
      cta:   "Open the Kin Network",
    },
    {
      Icon: Home,
      title: "Village & community heritage",
      body:  "Traditions, festivals, historic homes, and oral histories connected to the ancestral villages of families on Social Pulse.",
      to:    "/state/kerala",
      cta:   "Browse by state",
    },
    {
      Icon: ScrollText,
      title: "Preserving family traditions",
      body:  "Recipes, songs, heirlooms, oral histories, and customs passed through generations — captured before they slip away.",
      to:    "/family-trees",
      cta:   "Add a tradition",
    },
    {
      Icon: Feather,
      title: "Legacy builders",
      body:  "Profiles of individuals whose lives left a lasting mark on their families, villages, and communities.",
      to:    "/celebrate/browse",
      cta:   "Read legacy stories",
    },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="heritage-editorial">
      <SectionHeader
        Icon={Sparkles}
        color={P.gold}
        title="Living heritage sections"
        subtitle="Editorial spaces where families, historians, and neighbours contribute together."
      />
      <div className="grid md:grid-cols-2 gap-3">
        {BLOCKS.map((b) => {
          const Icon = b.Icon;
          return (
            <Link key={b.title} to={b.to}
                  className="rounded-xl border bg-white p-5 flex flex-col gap-2 hover:shadow-md transition"
                  style={{ borderColor: P.saffronSoft }}
                  data-testid={`heritage-block-${b.title.split(" ").join("-").toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" style={{ color: P.saffron }} />
                <span className="font-display text-lg">{b.title}</span>
              </div>
              <p className="text-sm" style={{ color: P.inkSoft }}>{b.body}</p>
              <span className="text-sm font-semibold flex items-center gap-1"
                    style={{ color: P.saffron }}>
                {b.cta} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                          MEMORY WALL                             */
/* ---------------------------------------------------------------- */
function MemoryWall() {
  const PROMPTS = [
    "Light a candle for someone you miss.",
    "Share a photo of your grandparents' wedding.",
    "Add a family recipe passed down through generations.",
    "Tell us about the village your family came from.",
    "Upload a letter, a certificate, or a piece of the family archive.",
  ];
  return (
    <section className="py-12 border-y"
             style={{ background: P.parchment, borderColor: P.saffronSoft }}
             data-testid="heritage-memory-wall">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <Church className="w-8 h-8 mx-auto opacity-70 mb-3" style={{ color: P.rose }} />
        <h2 className="font-display text-3xl">The Memory Wall</h2>
        <p className="mt-2 text-base" style={{ color: P.inkSoft }}>
          A community space where families leave tributes, light virtual candles,
          and share messages of remembrance for the people who came before them.
        </p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-left max-w-xl mx-auto">
          {PROMPTS.map((p, i) => (
            <li key={i}
                className="rounded-lg bg-white/70 backdrop-blur px-3 py-2 text-sm flex items-start gap-2"
                style={{ color: P.ink }}>
              <span style={{ color: P.rose }}>•</span> {p}
            </li>
          ))}
        </ul>
        <Link to="/celebrate/submit"
              className="inline-flex items-center gap-2 mt-6 rounded-full px-6 py-2.5 font-semibold text-white"
              style={{ background: P.rose }}
              data-testid="heritage-memory-wall-cta">
          <Flower2 className="w-4 h-4" /> Add to the Memory Wall
        </Link>
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                        RESEARCH CORNER                           */
/* ---------------------------------------------------------------- */
function ResearchCorner() {
  const GUIDES = [
    { Icon: Camera,   title: "Preserving old photographs", body: "How to scan, clean, and store century-old family photographs without damaging them." },
    { Icon: BookOpen, title: "Reading historical records",  body: "Church registers, land deeds, and census records — how to decode them and what they can tell you." },
    { Icon: Landmark, title: "Interviewing elders",          body: "Ten questions to ask your grandparents before their stories fade from living memory." },
    { Icon: ScrollText, title: "DNA & genealogy research", body: "How to interpret DNA test results and combine them with paper records to trace a lineage." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="heritage-research-corner">
      <SectionHeader
        Icon={BookOpen}
        color={P.green}
        title="Research corner"
        subtitle="Practical guides for anyone starting or deepening their family-history research."
      />
      <div className="grid md:grid-cols-2 gap-3">
        {GUIDES.map((g) => {
          const Icon = g.Icon;
          return (
            <div key={g.title}
                 className="rounded-xl border bg-white p-4 flex gap-3"
                 style={{ borderColor: P.saffronSoft }}
                 data-testid={`heritage-guide-${g.title.split(" ").join("-").toLowerCase()}`}>
              <Icon className="w-5 h-5 shrink-0 mt-1" style={{ color: P.green }} />
              <div>
                <div className="font-semibold">{g.title}</div>
                <p className="text-sm mt-1" style={{ color: P.inkSoft }}>{g.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                          BOTTOM CTA                              */
/* ---------------------------------------------------------------- */
function BottomCTA() {
  return (
    <section className="py-14"
             style={{ background: `linear-gradient(180deg, ${P.bg} 0%, ${P.parchment} 100%)` }}>
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="font-display text-3xl">Every family has a story worth keeping.</h2>
        <p className="mt-2" style={{ color: P.inkSoft }}>
          Whether you're documenting one grandmother's recipes or reconstructing
          a lineage across five generations, Social Pulse Heritage is built for
          the long haul — and for the community of families who care.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/family-trees/new"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-white"
                style={{ background: P.saffron }}
                data-testid="heritage-bottom-cta-tree">
            <TreePine className="w-4 h-4" /> Start a family tree
          </Link>
          <Link to="/obituaries/submit"
                className="inline-flex flex-col items-center rounded-full px-5 py-1.5 font-semibold text-white"
                style={{ background: "#8B5CF6" }}
                data-testid="heritage-bottom-cta-obituary">
            <span className="inline-flex items-center gap-2 leading-none">
              <Feather className="w-4 h-4" /> Add an Obituary
            </span>
            <span className="text-[11px] opacity-90 font-normal">(Death Announcement)</span>
          </Link>
          <Link to="/celebrate/submit"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-white"
                style={{ background: P.rose }}
                data-testid="heritage-bottom-cta-memorial">
            <Flower2 className="w-4 h-4" /> Create a memorial
          </Link>
        </div>
      </div>
    </section>
  );
}


/* ---------------------------------------------------------------- */
/*                       SMALL UTILITIES                            */
/* ---------------------------------------------------------------- */
function SectionHeader({ Icon, color, title, subtitle, seeMore }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
      <div>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <h2 className="font-display text-2xl">{title}</h2>
        </div>
        {subtitle && <p className="text-xs mt-1" style={{ color: P.inkSoft }}>{subtitle}</p>}
      </div>
      {seeMore && (
        <Link to={seeMore.to}
              className="text-sm font-semibold flex items-center gap-1 hover:underline"
              style={{ color }}>
          {seeMore.label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

function SubHeader({ color, title, subtitle }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] font-mono uppercase tracking-wider"
           style={{ color }}>
        {title}
      </div>
      {subtitle && <div className="text-xs opacity-70">{subtitle}</div>}
    </div>
  );
}

function EmptyBlock({ icon: Icon, label, cta }) {
  return (
    <div className="rounded-xl border bg-white p-8 text-center"
         style={{ borderColor: P.saffronSoft }}>
      <Icon className="w-8 h-8 mx-auto opacity-50 mb-2" />
      <p className="text-sm mb-3" style={{ color: P.inkSoft }}>{label}</p>
      {cta && (
        <Link to={cta.to}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
              style={{ background: P.saffron }}>
          {cta.label}
        </Link>
      )}
    </div>
  );
}

function SkeletonSection() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-10">
      <div className="rounded-xl border p-6 text-center opacity-60"
           style={{ borderColor: P.saffronSoft }}>
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    </section>
  );
}
